import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { AllowedGuard } from 'components/Tvendo/Common/UserAction';
import { ConstantPermissions } from 'contexts/useConstant';
import GridContainer from 'components/Grid/GridContainer';
import ExpiredInvoicesGrid from './ExpiredInvoicesGrid';
import Modal from 'components/Tvendo/Common/ModalBase';
import Button from "components/CustomButtons/Button";
import TextField from '@material-ui/core/TextField';
import { onlyMoney, nDecimal } from 'hooks/Pipe';
import GridItem from 'components/Grid/GridItem';
import { wsGetAll } from 'services/WService';
import { Autocomplete } from '@mui/material';
import Badge from 'components/Badge/Badge';
import { Box } from '@material-ui/core';

import PackagePriceModal from './PackagePriceModal';
import Visibility from "@material-ui/icons/Visibility";

export default function ModalApprove(props) {
  const { setOrder } = props;
  const order = props.model;
  const approveUrl = props.approveUrl || 'approve';

  const [dataChanging, setDataChanging] = useState(order);
  const [businessPartners, setBusinessPartners] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [expiredOrders, setExpiredOrders] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [interestRate, setInterestRate] = useState(parseFloat(order.interest_rate) || 0);
  const [digitateDiscount, setDigitateDiscount] = useState(
    parseFloat(order.digitate_discount) || 0,
  );

  async function listBusinessPartners() {
    let response = await wsGetAll('base/business-partner?CardType=S&QryGroup1=Y&QryGroup2=Y');
    if (response.success) setBusinessPartners(response.data);
  }

  async function listPaymentTerms() {
    let response = await wsGetAll('base/payment-term');
    if (response.success) setPaymentTerms(response.data);
  }

  async function listExpiredOrders() {
    let res = await wsGetAll(`sap/business-partner/expired-invoices?CardCode=${order.CardCode}`);
    if(res.success){
      setExpiredOrders(res.data);
    }
  }

  const handleChange = (field, value) => {
    setDataChanging({
      ...dataChanging,
      [field]: value,
    });
  };

  useEffect(() => {
    setDigitateDiscount(parseFloat(order.digitate_discount) || 0);
  }, [order.digitate_discount]);

  useEffect(() => {
    setInterestRate(parseFloat(order.interest_rate) || 0);
  }, [order.interest_rate]);

  const solicitudMayorista = useMemo(() => {
    if(typeof order.comment !== 'string') return false;
    let str = order.comment.toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return str.includes('MAYORISTA');
  }, [order.comment]);

  useEffect(() => {
    if (order.open) {
      if(businessPartners.length === 0) listBusinessPartners();
      if(paymentTerms.length === 0) listPaymentTerms();
      if (
        order.retention_reasons?.includes('tiene ordenes vencidas') ||
        order.retention_reasons?.includes('su línea de crédito')
      ) {
        listExpiredOrders();
      }
    }else{
      setExpiredOrders(null);
    }
  }, [order.open]);

  const entityUrl = useMemo(() => {
    let url = '';
    if(order.ObjType === '14'){
      url = `sale/credit-note/${order.id}/approve`;
    }else{
      url = `ordm/order/${order.id}/${approveUrl}`;
    }
    return url;
  }, [order.id, order.ObjType]);

  return (
    <Modal
      title="Rellene esta información para aprobar"
      model={{ open: order.open }}
      setOpen={props.setOpen}
      showNotify={props.showNotify}
      reload={props.reload}
      dataChanging={dataChanging}
      setDataChanging={setDataChanging}
      entity={entityUrl}
      fullWidth
      maxWidth={'lg'}
      saveButtonLabel="Aprobar"
      closeButtonLabel="Cancelar"
    >
      <PackagePriceModal
        open={modalOpen}
        setOpen={setModalOpen}
        order={order}
        showNotify={props.showNotify}
        setOrder={setOrder}
      />
      <Box component={'div'}>
        <GridContainer>
          <GridItem xs={12}>
            {order.ObjType === '14' ? 'Nota de crédito' : 'Orden'} {order.id}
          </GridItem>
          <GridItem xs={3}>
            <h6>Vendedor</h6>
            <h5>{order.SlpName}</h5>
          </GridItem>

          <GridItem xs={1}>
            <h6>Canal:</h6>
            <h5>{order.base_canal_description}</h5>
          </GridItem>

          <GridItem xs={3}>
            <h6>Cliente:</h6>
            <h5>{order.CardName}</h5>
          </GridItem>

          <GridItem xs={2}>
            <h6>Solicitud de descuento:</h6>
            <Badge color={order.digitate_discount == 0 ? 'danger' : 'success'}>
              {order.digitate_discount == 0 ? 'No' : 'Si'}
            </Badge>
            {order.digitate_discount != 0 && <span> {nDecimal(order.digitate_discount)}%</span>}
          </GridItem>

          <GridItem xs={2}>
            <h6>Solicitud de crédito:</h6>
            <Badge color={order.request_credit === 'N' ? 'danger' : 'success'}>
              {order.request_credit === 'N' ? 'No' : 'Si'}
            </Badge>
          </GridItem>

          <GridItem xs={2}>
            <h6>Solicitud canal mayorista:</h6>
            <Badge color={!solicitudMayorista ? 'danger' : 'success'}>
              {!solicitudMayorista ? 'No' : 'Si'}
            </Badge>

            {solicitudMayorista && (
              <Button justIcon round simple color="primary" onClick={() => setModalOpen(true)}>
                <Visibility />
              </Button>
            )}
          </GridItem>
          <GridItem xs={4}>
            <TextField
              label="Comentario aprobación"
              fullWidth
              multiline
              inputProps={{ max: 80 }}
              value={order.approve_comment}
              onChange={(e) => {
                let value = e.target.value;
                if (value.length === 0) {
                  value = null;
                }
                handleChange('approve_comment', value);
              }}
            />
          </GridItem>

          <GridItem xs={4}>
            <h6>Mesa:</h6>
            <h5>{order.base_bureau_description}</h5>
          </GridItem>

          {order.retention_reasons !== null && (
            <GridItem xs={12}>
              <h6>Motivo retención:</h6>
              {order.retention_reasons?.split(', ').map((reason, index) => {
                if (
                  reason.includes('tiene ordenes vencidas') ||
                  reason.includes('su línea de crédito')
                ) {
                  return (
                    <Fragment key={index}>
                      <h5>- {reason}</h5>

                      {expiredOrders !== null && expiredOrders.length === 0 ? (
                        <h6>No se encontraron las ordenes vencidas</h6>
                      ) : (
                        <ExpiredInvoicesGrid documents={expiredOrders} />
                      )}
                    </Fragment>
                  );
                }
                return <h5 key={index}>- {reason}</h5>;
              })}
            </GridItem>
          )}

          {order.freight_rate !== null && (
            <AllowedGuard permission={ConstantPermissions.BASE_ROUTES_FREIGHT_RATE}>
              <GridItem xs={12}>
                <h6>Flete ruta: </h6>
                <h5>{order.freight_rate}</h5>
              </GridItem>
            </AllowedGuard>
          )}

          <GridItem xs={12}>
            <h6>Comentario vendedor: </h6>
            <h5>{order.comment}</h5>
          </GridItem>

          <GridItem xs={4}>
            <h6>Total:</h6>
            <h5>S/ {nDecimal(order.total_amount)}</h5>
          </GridItem>

          <GridItem xs={4}>
            <h6>Total con interés ({interestRate}%):</h6>
            <h5>S/ {nDecimal(order.total_amount * (1 + interestRate / 100))}</h5>
          </GridItem>

          <GridItem xs={4}>
            <h6>Total con descuento ({digitateDiscount}%):</h6>
            <h5>
              S/{' '}
              {nDecimal(
                order.total_amount * (1 + interestRate / 100) * (1 - digitateDiscount / 100),
              )}
            </h5>
          </GridItem>

          <GridItem xs={4}></GridItem>
          <GridItem xs={4}>
            <h6>Monto interés: </h6>
            <h5>
              S/ {nDecimal(order.total_amount * (1 + interestRate / 100) - order.total_amount)}
            </h5>
          </GridItem>
          <GridItem xs={4}>
            <h6>Monto descuento: </h6>
            <h5>
              S/{' '}
              {nDecimal(
                order.total_amount * (1 + interestRate / 100) -
                  order.total_amount * (1 + interestRate / 100) * (1 - digitateDiscount / 100),
              )}
            </h5>
          </GridItem>
        </GridContainer>
      </Box>

      {order.ObjType === '13' && (
        <>
          {order.digitate_discount > 0 || order.request_credit === 'Y' ? (
            <Autocomplete
              options={businessPartners}
              getOptionLabel={(option) => option.CardName}
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label="Socio de Negocio" margin="normal" required />
              )}
              onChange={(e, item) => handleChange('discount_business_partner_id', item.id)}
            />
          ) : undefined}

          {order.digitate_discount > 0 ? (
            <TextField
              label="Descuento"
              type="number"
              name="discount"
              margin="normal"
              fullWidth
              value={digitateDiscount}
              onInput={(e) => setDigitateDiscount(e.target.value)}
              inputProps={{ min: 0, step: 0.0001 }}
              onKeyPress={onlyMoney}
              required
            />
          ) : undefined}

          {order.request_credit === 'Y' ? (
            <Fragment>
              <Autocomplete
                options={paymentTerms}
                getOptionLabel={(option) => option.PymntGroup}
                disableClearable
                renderInput={(params) => (
                  <TextField {...params} label="Término de pago" margin="normal" required />
                )}
                onChange={(e, item) => handleChange('base_payment_term_id', item.id)}
              />

              <TextField
                label="Interes %"
                type="number"
                name="interest_rate"
                margin="normal"
                fullWidth
                value={interestRate}
                onInput={(e) => setInterestRate(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                onKeyPress={onlyMoney}
                required
              />
            </Fragment>
          ) : undefined}
        </>
      )}
    </Modal>
  );
}
